import { LDFlagSet } from "launchdarkly-js-sdk-common";

import { Plan, PlanType } from "../../../../@types/index";
import { isWithinInterval, endOfDay } from "date-fns";
import { SpecialEnrollmentReason } from "./SpecialEnrollmentReason";

/** PlanReasonFilter defines a function that returns true to display the
 * SEP reason based on the given Plan, otherwise, returns false to
 * hide the SEP reason
 */
type PlanReasonFilter = (plan: Plan) => boolean;

const showWhenPDPPlanReasonFilter: PlanReasonFilter = (plan: Plan) =>
  plan.plan_type === PlanType.PDP;

const hideWhenPDPPlanReasonFilter: PlanReasonFilter = (plan: Plan) =>
  !showWhenPDPPlanReasonFilter(plan);

const showWhenMAPDOrPDPPlanReasonFilter: PlanReasonFilter = (plan: Plan) =>
  plan.plan_type === PlanType.MAPD || plan.plan_type === PlanType.PDP;

const showWhenMAPlanReasonFilter: PlanReasonFilter = (plan: Plan) =>
  plan.plan_type === PlanType.MA;

const showWhenMoreThanThreeStarsPlanReasonFilter: PlanReasonFilter = (
  plan: Plan
) => plan.overall_star_rating?.rating >= 3;

// Spec indicates five stars, but code uses high_performing
// Row 28
// https://confluence.cms.gov/pages/viewpage.action?spaceKey=MCT&title=CY2022+Enrollment+SEP+Questions
// https://adhoc.slack.com/archives/CGTL5QWP8/p1633722748097400
const showWhenHighPerformingPlanReasonFilter: PlanReasonFilter = (plan: Plan) =>
  plan.high_performing;

const planReasonFilters: Record<string, PlanReasonFilter> = {
  hideWhenPDP: hideWhenPDPPlanReasonFilter,
  showWhenPDP: showWhenPDPPlanReasonFilter,
  showWhenMA: showWhenMAPlanReasonFilter,
  showWhenMAPDOrPDP: showWhenMAPDOrPDPPlanReasonFilter,
  showWhenMoreThanThreeStars: showWhenMoreThanThreeStarsPlanReasonFilter,
  showWhenHighPerforming: showWhenHighPerformingPlanReasonFilter,
};

/**
 * Visibility of SEP Reasons are based on three things:
 * - SpecialEnrollmentReason.ldFlag
 *   - optional LD flag name having boolean value
 *   - overrides everything. It serves as a quick way to show/hide SEP reasons.
 * - SpecialEnrollmentReason.showDates
 *   - specifies the period to display the SEP reason
 * - SpecialEnrollmentReason.planFilter
 */
export function showSEPReason(
  flags: LDFlagSet,
  plan: Plan,
  reason: SpecialEnrollmentReason,
  today: Date = new Date()
): boolean {
  let displayChoice = true;
  if (reason.ldFlag) {
    displayChoice = flags[reason.ldFlag];
  } else {
    if (reason.showDates) {
      const {
        month: startMonth,
        day: startDay,
        year: startYear,
      } = reason.showDates.start;
      const {
        month: endMonth,
        day: endDay,
        year: endYear,
      } = reason.showDates.end;
      const start = new Date(
        startYear || today.getFullYear(),
        startMonth - 1,
        startDay
      );
      const end = endOfDay(
        new Date(endYear || today.getFullYear(), endMonth - 1, endDay)
      );

      displayChoice = isWithinInterval(today, { start, end });
    }
    if (displayChoice && reason.planReasonFilter) {
      displayChoice = planReasonFilters[reason.planReasonFilter](plan);
    }
  }

  return displayChoice;
}
